import { useMemo } from 'react';
import type { CalendarEvent, MultiDayEventSpan } from '../../../../types';
import { useCalculateMultiDayEventSpans } from './useCalculateMultiDayEventSpans';

/**
 * Organize multi-day event spans into rows to handle overlaps
 * Events that don't overlap can be placed in the same row
 */
export const useOrganizeEventSpansIntoRows = <T extends CalendarEvent>({
  days,
  events,
}: {
  days: Date[];
  events: T[];
}): Array<Array<MultiDayEventSpan<T>>> => {
  const spans = useCalculateMultiDayEventSpans<T>({ days, events });

  return useMemo(() => {
    if (spans.length === 0) {
      return [];
    }

    const rows: Array<Array<MultiDayEventSpan<T>>> = [];

    for (const span of spans) {
      // Find a row where this event doesn't overlap
      let isPlaced = false;
      for (const row of rows) {
        const hasOverlap = row.some(
          (existingSpan) =>
            !(span.endDayIndex < existingSpan.startDayIndex || span.startDayIndex > existingSpan.endDayIndex),
        );

        if (!hasOverlap) {
          row.push(span);
          isPlaced = true;
          break;
        }
      }

      if (!isPlaced) {
        rows.push([span]);
      }
    }

    return rows;
  }, [spans]);
};
